import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Globe, Loader2, Quote, Target, TrendingUp } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../i18n/translations";
import { SERVICES } from "../data/mock";
import { fetchReference, fetchReferences, imgUrl } from "../lib/api";
import { SITE_URL } from "../hooks/useSEO";
import Seo, { ORG_JSONLD } from "../components/Seo";
import Reveal from "../components/Reveal";
import RefLogo from "../components/RefLogo";
import ReferenceCard from "../components/ReferenceCard";

const ReferenceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const t = translations[lang].references;
  const [ref, setRef] = useState(null);
  const [others, setOthers] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    Promise.all([fetchReference(slug), fetchReferences()])
      .then(([r, all]) => {
        // Reference resolved via an old/renamed slug → redirect to canonical URL
        if (r && r.slug && r.slug !== slug) {
          navigate(`/referanslar/${r.slug}`, { replace: true });
          return;
        }
        setRef(r);
        setOthers(all.filter((x) => x.slug !== slug).slice(0, 3));
        setStatus("ok");
      })
      .catch(() => setStatus("error"));
  }, [slug, navigate]);

  if (status === "loading") {
    return <div className="min-h-screen bg-navy-950 flex items-center justify-center text-slate-400"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }
  if (status === "error" || !ref) {
    return (
      <div className="min-h-screen bg-navy-950 pt-40 text-center px-5" data-testid="reference-not-found">
        <Seo title={t.notFound} description={t.notFound} noindex />
        <h1 className="font-display font-bold text-3xl text-white">{t.notFound}</h1>
        <Link to="/referanslar" className="mt-6 inline-flex items-center gap-2 text-amber-400 font-semibold"><ArrowLeft className="w-4 h-4" /> {t.backAll}</Link>
      </div>
    );
  }

  const service = SERVICES.find((s) => s.id === ref.service);
  const paragraphs = (ref.content?.[lang] || "").split(/\n\s*\n/).filter(Boolean);
  const title = `${ref.name} — ${ref.result?.[lang] || t.pageTitle}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      articleSection: "Case Study",
      headline: title,
      description: ref.summary?.[lang],
      image: ref.logo ? imgUrl(ref.logo) : ORG_JSONLD.logo,
      inLanguage: lang,
      mainEntityOfPage: `${SITE_URL}/referanslar/${ref.slug}`,
      author: { "@type": "Organization", name: "AdalCrea", url: SITE_URL },
      publisher: { "@type": "Organization", name: "AdalCrea", logo: { "@type": "ImageObject", url: ORG_JSONLD.logo } },
      about: { "@type": "Organization", name: ref.name, ...(ref.website ? { url: ref.website } : {}) },
      datePublished: ref.created_at?.slice?.(0, 10),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: lang === "tr" ? "Anasayfa" : "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: t.pageTitle, item: `${SITE_URL}/referanslar` },
        { "@type": "ListItem", position: 3, name: ref.name, item: `${SITE_URL}/referanslar/${ref.slug}` },
      ],
    },
  ];

  return (
    <>
      <Seo title={title} description={ref.summary?.[lang]} path={`/referanslar/${ref.slug}`} lang={lang} type="article" image={ref.logo ? imgUrl(ref.logo) : undefined} jsonLd={jsonLd} />

      <section className="relative bg-navy-950 pt-36 pb-16 lg:pt-44 lg:pb-20 overflow-hidden" data-testid="reference-detail">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="absolute -top-32 right-0 w-[30rem] h-[30rem] rounded-full bg-amber-500/10 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <Link to="/referanslar" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-400 transition-colors" data-testid="reference-back">
            <ArrowLeft className="w-4 h-4" /> {t.backAll}
          </Link>
          <div className="mt-8 grid lg:grid-cols-3 gap-10 items-end">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-4">
                <RefLogo name={ref.name} logo={ref.logo} size="lg" dark />
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-500">{ref.sector?.[lang]}</span>
                  <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-white leading-[1.1] mt-1" data-testid="reference-detail-name">{ref.name}</h1>
                </div>
              </div>
              <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl">{ref.summary?.[lang]}</p>
              <div className="mt-6 flex flex-wrap gap-3 text-sm">
                {service && (
                  <Link to={`/hizmetlerimiz/${service.slug}`} className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-slate-200 hover:border-amber-500/40">
                    <Target className="w-4 h-4 text-amber-400" /> {service.title[lang]}
                  </Link>
                )}
                {ref.website && (
                  <a href={ref.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-slate-200 hover:border-amber-500/40">
                    <Globe className="w-4 h-4 text-amber-400" /> {t.website}
                  </a>
                )}
              </div>
            </div>
            {ref.metric && (
              <div className="rounded-3xl bg-amber-500 p-8 text-navy-950">
                <TrendingUp className="w-7 h-7" />
                <div className="font-display font-extrabold text-5xl lg:text-6xl mt-4 leading-none">{ref.metric}</div>
                <div className="mt-2 font-semibold">{ref.metric_label?.[lang]}</div>
                <div className="mt-4 pt-4 border-t border-navy-950/20 text-sm font-medium">{ref.result?.[lang]}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-12">
          <article className="lg:col-span-2 max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-500">
              <span className="w-6 h-px bg-amber-500" /> {t.story}
            </span>
            <div className="mt-6 space-y-6 text-slate-700 text-lg leading-relaxed" data-testid="reference-detail-content">
              {paragraphs.length > 0 ? paragraphs.map((p, i) => <Reveal as="p" key={i} delay={i * 60}>{p}</Reveal>) : <p>{ref.summary?.[lang]}</p>}
            </div>
            <div className="mt-10 rounded-2xl bg-slate-50 border border-slate-200 p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-amber-500">{t.result}</p>
              <p className="mt-2 font-display font-bold text-xl text-navy-900">{ref.result?.[lang]}</p>
            </div>
            {ref.testimonial?.[lang] && (
              <figure className="mt-8 relative rounded-3xl bg-navy-900 p-8 lg:p-10 overflow-hidden" data-testid="reference-detail-testimonial">
                <div className="absolute inset-0 dot-pattern opacity-30" />
                <Quote className="relative w-10 h-10 text-amber-500/40" />
                <blockquote className="relative mt-4 font-display text-xl lg:text-2xl text-white leading-snug">“{ref.testimonial[lang]}”</blockquote>
                {ref.contact_name && (
                  <figcaption className="relative mt-6 flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-amber-500 text-navy-950 font-bold flex items-center justify-center text-sm">
                      {ref.contact_name.split(" ").filter((w) => !w.endsWith(".")).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
                    </span>
                    <div>
                      <p className="text-white font-semibold">{ref.contact_name}</p>
                      <p className="text-slate-400 text-sm">{ref.contact_title?.[lang]}{ref.contact_title?.[lang] ? ", " : ""}{ref.name}</p>
                    </div>
                  </figcaption>
                )}
              </figure>
            )}
          </article>
          <aside className="space-y-6">
            {service && (
              <div className="rounded-2xl border border-slate-200 p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{t.serviceUsed}</p>
                <h3 className="mt-2 font-display font-bold text-lg text-navy-900">{service.title[lang]}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{service.short[lang]}</p>
                <Link to={`/hizmetlerimiz/${service.slug}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 hover:gap-2.5 transition-all">
                  {translations[lang].servicesOverview.learnMore} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
            <div className="rounded-2xl bg-navy-900 p-6">
              <h3 className="font-display font-bold text-lg text-white">{t.ctaTitle}</h3>
              <p className="mt-2 text-sm text-slate-300">{t.ctaDesc}</p>
              <Link to="/iletisim" className="mt-5 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-navy-950 font-semibold px-5 py-3 rounded-lg transition-colors text-sm" data-testid="reference-detail-cta">
                {t.ctaButton} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {others.length > 0 && (
        <section className="bg-slate-50 py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <h2 className="font-display font-bold text-2xl lg:text-3xl text-navy-900">{t.others}</h2>
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {others.map((r, i) => (
                <Reveal key={r.id} delay={i * 90}><ReferenceCard r={r} /></Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default ReferenceDetail;
